import { useAuthUser } from "@/core/context/AuthUserContext"
import { useCookieConsent } from "@/organization/tracking/cookie-consent/CookieConsentStore"
import { useTrackingTransparency } from "@/product/util/hook/useTrackingTransparency"
import Relay from "@/relay/relayUtils"
import Tracker from "@openreplay/tracker"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import React, { useEffect } from "react"

const OpenReplayContext = React.createContext(false)

const OpenReplayProvider: React.FC = ({ children }) => {
  const { authUser } = useAuthUser()
  const { hasConsented } = useCookieConsent()
  const canTrack = useTrackingTransparency()
  const [tracker, setTracker] = React.useState<Tracker | null>(null)
  const { openReplay } = useFeatureFlags()

  const shouldInitializeOpenReplay =
    !!OPEN_REPLAY_PROJECT_ID &&
    !/headless/i.test(navigator.userAgent) &&
    hasConsented &&
    canTrack

  useEffect(() => {
    if (!openReplay) return

    const startOpenReplay = async () => {
      if (!tracker && OPEN_REPLAY_PROJECT_ID && authUser?.id) {
        try {
          const t = new Tracker({
            projectKey: OPEN_REPLAY_PROJECT_ID,
            ingestPoint: "https://discoproduction.us.openreplay.cloud/ingest",
            disableStringDict: true,
          })
          if (!t.isActive()) {
            setTracker(t)
            await t.start({
              userID: Relay.rawId(authUser.id),
            })
          }
        } catch (error) {
          console.error("OpenReplay error:", error)
        }
      }
    }
    startOpenReplay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldInitializeOpenReplay])

  useEffect(() => {
    if (!openReplay) return

    // if auth user changes, ensure we re-identify the auth-user
    if (authUser?.id && shouldInitializeOpenReplay && tracker)
      tracker.setUserID(Relay.rawId(authUser.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, shouldInitializeOpenReplay])

  return (
    <OpenReplayContext.Provider value={shouldInitializeOpenReplay}>
      {children}
    </OpenReplayContext.Provider>
  )
}

export { OpenReplayContext, OpenReplayProvider }
